/**
 * What the Create ad wizard says, where a runner can reach it — for the reason
 * `overview-copy.ts` gives. `pages/CreateAd.tsx` renders these and decides nothing
 * about them; the decisions are in `create-ad.ts`.
 *
 * 🔴 Three sentences here are claims about X, and each is bounded to what this
 * console has seen rather than stated as X's rule: an account that is not ACCEPTED
 * (`format.ts` gives a meaning to ACCEPTED and to nothing else), an instrument with no
 * payment method (a create on one answered 201 on 2026-10-21, `audits/2026-10-21/
 * launch.txt`; whether anything spends on it has not been observed), and the number
 * Ads Manager shows for a campaign (observed twice, `create-ad.ts`). Nothing here
 * says what Start does beyond asking X for ACTIVE: the sentence for that is
 * `START_IS_NOT_DELIVERY` in `targeting-copy.ts`, rendered verbatim, and the campaign's
 * answer is `servableText` in `campaign-copy.ts`.
 *
 * 🔴 Two sentences are borrowed, not rewritten: a campaign or a line item that
 * `exists` after a stopped run says `EXISTS` from `campaign-copy.ts` and
 * `line-item-copy.ts`, whose tests check them against the contract. The criterion and
 * the promoted tweet have no such sentence on main, so theirs are written here in the
 * same words.
 *
 * `create-ad-copy.test.ts` holds what these strings must say and must not: «created
 * PAUSED», «X decides whether it serves» and «do not retry» are present; nothing says
 * «delivered», «live» or «running» about Start; no superlative about this console's
 * history; no field label says «micro».
 */
import type { FundingInstrument, LineItemObjective } from "./api.ts";
import { EXISTS as CAMPAIGN_EXISTS, UNKNOWN as CAMPAIGN_UNKNOWN } from "./campaign-copy.ts";
import {
	AD_MEDIA_MAX,
	AD_NAME_MAX,
	type ImageSize,
	type Problem,
	type StepState,
	adsManagerNumber,
	urlRequired,
} from "./create-ad.ts";
import { approvalNote } from "./creative-copy.ts";
import {
	EXISTS as LINE_ITEM_EXISTS,
	UNKNOWN as LINE_ITEM_UNKNOWN,
	OBJECTIVES,
} from "./line-item-copy.ts";
import { DELIVERY_PROBLEMS } from "./line-item-delivery-copy.ts";
import { PLACEMENT_PROBLEMS, SEARCH_NEEDS_KEYWORD } from "./line-item-fields-copy.ts";
import { microMoney } from "./money.ts";
import { PRIMARY_LIMITS } from "./primary-targeting.ts";

export const TITLE = "Create ad";

export const INTRO =
	"Six steps on one screen, and nothing is sent to X until Create (paused) on the Review step. Everything that press makes is created PAUSED — a campaign, a line item under it, its countries, and the tweet as the ad — and Start is a separate press afterwards. X decides whether it serves.";

export const STEP_NAMES = [
	"Ad account",
	"Campaign",
	"Ad group",
	"Audience",
	"Ad",
	"Review",
] as const;

export const NEXT = "Next";
export const BACK = "Back";

/** The read-only line: the gate it actually is, as `campaign-copy.ts` words it. */
export const READ_ONLY =
	"You can read this ad account. Creating an ad needs write access to it — the same access Create on the Campaigns screen needs.";

// ── Step 0: ad account ──────────────────────────────────────────────

export const ACCOUNT_HELP =
	"The ad account everything is created under. Its status is the mirror's — what Sync from X wrote — with the time it was read.";

/**
 * 🔴 A boundary of this console, not a statement about X: `approvalStatus` in
 * format.ts gives a meaning to ACCEPTED and to no other value, and what X does with
 * a campaign under a REJECTED account has not been observed here. The step is not
 * blocked — X is the arbiter and the creates go as asked.
 */
export function accountNotAccepted(status: string | null): { title: string; body: string } | null {
	if (status === "ACCEPTED") return null;
	return {
		title: status
			? `This account is ${status} in the mirror, not ACCEPTED.`
			: "This account has no approval status in the mirror yet.",
		body: "Only ACCEPTED is approved; what X does with the rest this console has not seen. Nothing here stops you: the creates below are sent as asked, and X decides whether it serves — the Served column on Campaigns carries its answer.",
	};
}

// ── Step 1: campaign ────────────────────────────────────────────────

export const CAMPAIGN_HELP =
	"A name, the funding instrument that pays, and the budget as you would write it on an invoice — whole units and up to two decimals, in the instrument's currency. This screen turns it into the unit X holds budgets in; you never type that here.";

export const NAME_LABEL = "Name";
export const INSTRUMENT_LABEL = "Funding instrument";

/**
 * 🔴 **The signal is `ableToFund` and `reasonsNotAbleToFund`, never the description**
 * (A6, 2026-10-21). Until that date this function read the description and said, over
 * `1doaxx`: *«This instrument has no card, by its own description.» — «X accepted a
 * create on it on 2026-10-21; whether it spends is X's. This console cannot add one.»*
 * Both halves are now contradicted by a reading: `1doaxx` answers `ableToFund: true`,
 * `reasonsNotAbleToFund: []` **and** X billed campaign `phbin` on it —
 * `billedChargeLocalMicro` 30652184 over 2026-10-14T14:00Z→21T14:00Z
 * (`audits/2026-10-21/stats-read.txt`, lines 7 and 38–43) — while its description still
 * read «(no payment method has been set up yet)». So the description is X's text and
 * nothing more; what funds and what does not is X's own verdict on the instrument, with
 * X's reasons as X gave them, and this console adds no reading of its own.
 *
 * The reference agrees (audits/2026-10-22/x-reference-funding-verbatim.md): the guide puts
 * `description` among the «Descriptive» attributes and names `able_to_fund` and
 * `reasons_not_able_to_fund` as «Funding ability», and X's own sandbox POST example pairs
 * «(no payment method has been set up yet)» with `able_to_fund: true`. 🔵 It does not say
 * those two fields are all that decides — credit remaining, end_time and type are not
 * placed under them, and nothing says whether able_to_fund counts them — so the note
 * names them as X's fields for this, not as the whole answer (2026-10-22).
 */
export function instrumentWarning(
	f: Pick<FundingInstrument, "ableToFund" | "reasonsNotAbleToFund" | "description"> | null,
): { variant: "warning" | "info"; title: string; body: string } | null {
	if (f === null) return null;
	if (!f.ableToFund) {
		const reasons =
			f.reasonsNotAbleToFund.length > 0
				? `X's reasons, as X gave them: ${f.reasonsNotAbleToFund.join(", ")}.`
				: "X gave no reason.";
		return {
			variant: "warning",
			title: "X says this instrument cannot fund (ableToFund: false).",
			body: `${reasons} A campaign on it is created PAUSED like any other; whether X serves it is X's answer, and this console cannot change the instrument.`,
		};
	}
	if (f.description !== null && /no payment method/i.test(f.description)) {
		return {
			variant: "info",
			title: `X describes this instrument as «${f.description}» and says it can fund (ableToFund: true).`,
			body: `The description is X's text, not a verdict on funding: on 2026-10-14…21 X billed ${microMoney(30_652_184, "EUR")} to campaign phbin on an instrument with this exact description and ableToFund: true (audits/2026-10-21/stats-read.txt). X's own fields for this are ableToFund and reasonsNotAbleToFund (the guide's «Funding ability») — shown here as X reports them.`,
		};
	}
	return null;
}

export function budgetLabel(which: "Daily" | "Total"): string {
	return `${which} budget`;
}

/** Under each budget field: the currency, and the two-decimal form. */
export function budgetHelper(currency: string | null, optional: boolean): string {
	const unit = currency ? `In ${currency}` : "In the instrument's currency";
	return optional
		? `${unit}, as 10 or 10.50. Optional — leave it empty and none is sent.`
		: `${unit}, as 10 or 10.50.`;
}

// ── Step 2: ad group ────────────────────────────────────────────────

export const AD_GROUP_HELP =
	"X's line item: the objective, the placement and the flight. The objective is yours, from the two v1 offers; the placement and the product are fixed by this console; the flight is yours.";

export const OBJECTIVE_LABEL = "Objective";

/** The line item's name — optional on X's POST and PUT alike (twads#171); the campaign card edits it too. */
export const AD_GROUP_NAME_LABEL = "Ad group name";
export const AD_GROUP_NAME_HELP =
	"Optional — X's name for the line item (the reference: «The name for the line item»). Sent only when given; it can be changed later on the campaign card.";

/** The line item's own total — optional on X's POST and PUT; the daily one is not offered (2026-10-22). */
export const AD_GROUP_TOTAL_LABEL = "Ad group total budget";

/**
 * Beside the field: the guide's own sentence on it, said and not checked — the worker does
 * not refuse a total above the campaign's, because X documents a consequence, not a refusal.
 */
export function adGroupTotalHelp(currency: string | null): string {
	const unit = currency ? `In ${currency}` : "In the instrument's currency";
	return `${unit}, as 10 or 10.50. Optional — leave it empty and none is sent. X's guide: «The total_budget_amount_local_micro for a line item cannot exceed the total budget for its parent campaign… Setting these values incorrectly may put the overall campaign into a paused and unservable state.»`;
}

// ── Where the daily budget lives (2026-10-23) ─────────────────────────

export const BUDGET_ON_LABEL = "Daily budget on";

/**
 * The two places, in the wizard's order. 🔴 «As Ads Manager does» is an observation, not the
 * reference: Ads Manager refused a console-made campaign with «Ad group 1: Daily budget is
 * required», and its Edit campaign shows no budget field (2026-10-23).
 */
export const BUDGET_ON_OPTIONS = [
	{ value: "ad-group", label: "Each ad group (as Ads Manager does)" },
	{ value: "campaign", label: "The campaign only" },
] as const;

/**
 * 🔴 Two observations of 2026-10-23 (-3d, A9) decide what the campaign step asks for with the
 * budget on each ad group: LINE_ITEM with a campaign daily budget was refused — «INVALID:
 * Please remove your daily campaign budget before turning on campaign budget optimization»,
 * request 6e24d969badff983 — and LINE_ITEM with a campaign total only was accepted (pi6aq,
 * 15:43:01Z). The reference calls the campaign's daily budget «required for most Funding
 * Instrument types»; X's answer on this account is the opposite beside LINE_ITEM.
 */
export const BUDGET_ON_HELP =
	"Each ad group: the campaign is created with X's campaign budget optimization (budget_optimization LINE_ITEM) and no daily budget — X refused one beside it on 2026-10-23 («Please remove your daily campaign budget before turning on campaign budget optimization») — and the ad group step asks for its own daily budget. A campaign total is optional: X accepted LINE_ITEM with a total only (pi6aq, 2026-10-23). The campaign only: the campaign's daily budget is required here, no budget optimization is sent and X sets its own (every campaign this console made that way came back CAMPAIGN), and the ad group has no daily budget.";

export const AD_GROUP_DAILY_LABEL = "Ad group daily budget";

/** Beside the field: required here, and the reference's own note on it, said and not checked. */
export function adGroupDailyHelp(currency: string | null): string {
	const unit = currency ? `In ${currency}` : "In the instrument's currency";
	return `${unit}, as 10 or 10.50. Required with the budget on each ad group. The reference: «This should be less than or equal to the total_budget_amount_local_micro». The console does not check it — X does, and what X saves is read back.`;
}

/**
 * The guide's sentence again, as a warning, when the two figures typed on this screen
 * already break it. `null` when either is absent: an absent campaign total is no limit
 * this screen can compare against.
 */
export function adGroupOverCampaign(
	adGroupTotal: number | null,
	campaignTotal: number | null,
): string | null {
	if (adGroupTotal === null || campaignTotal === null || adGroupTotal <= campaignTotal) return null;
	return "The ad group's total is above the campaign's. X's guide: «The total_budget_amount_local_micro for a line item cannot exceed the total budget for its parent campaign… Setting these values incorrectly may put the overall campaign into a paused and unservable state.» It is sent as typed; X decides.";
}

/**
 * 🔴 The two objectives v1 offers, under the names Ads Manager shows for them, and the
 * six it does not, listed so nobody looks for them elsewhere on this screen.
 *
 * The values are `OBJECTIVES` from line-item-copy.ts — the contract's closed list,
 * which the worker's `LINE_ITEM_ALLOWED` holds with its source — and this module adds
 * only the names seen: «Engagements» was read in the owner's Ads Manager screenshot of
 * 2026-10-08 (`audits/2026-10-08/x-ads-manager-observed.md`, the pergn row).
 * WEBSITE_CLICKS has no name here: «Website traffic» has been seen only as the NAME of
 * the owner's campaigns (2026-10-19, 2026-10-21), never as the objective's label on that
 * screen, so the API value stands until a screenshot shows the label. The six
 * others are X's API values from the reference's objective list
 * (`audits/2026-10-20/x-line-item-vocabulary.md` (а), read 2026-10-20) and carry no
 * Ads Manager name here, because none has been seen on that screen; they are offered
 * disabled, with the boundary as the label — not «X forbids», which was not measured.
 */
const ADS_MANAGER_NAME: Partial<Record<LineItemObjective, string>> = {
	ENGAGEMENTS: "Engagements",
};

/** The name Ads Manager was seen to show, with the API value — or the API value alone. */
function objectiveName(objective: LineItemObjective): string {
	const name = ADS_MANAGER_NAME[objective];
	return name === undefined ? objective : `${name} (${objective})`;
}

export const OBJECTIVES_NOT_OFFERED = [
	"APP_ENGAGEMENTS",
	"APP_INSTALLS",
	"REACH",
	"FOLLOWERS",
	"VIDEO_VIEWS",
	"PREROLL_VIEWS",
] as const;

export const NOT_OFFERED = "not offered: this console has not seen X accept it";

export const OBJECTIVE_OPTIONS: ReadonlyArray<{ value: string; label: string; disabled: boolean }> =
	[
		...OBJECTIVES.map((o) => ({
			value: o.value,
			label: objectiveName(o.value),
			disabled: false,
		})),
		...OBJECTIVES_NOT_OFFERED.map((value) => ({
			value,
			label: `${value} — ${NOT_OFFERED}`,
			disabled: true,
		})),
	];

/** Under the select: the API value that goes on the wire for the name chosen. */
export function objectiveHint(objective: LineItemObjective): string {
	const name = ADS_MANAGER_NAME[objective];
	return name === undefined
		? `Sent to X as objective ${objective}.`
		: `Sent to X as objective ${objective}. Ads Manager shows it as «${name}».`;
}

/** The review row: the name and the value, so the row reads the same as the select did. */
export function objectiveSummary(objective: LineItemObjective): string {
	return objectiveName(objective);
}

export const START_LABEL = "Start";
export const END_LABEL = "End";

export const SCHEDULE_HELP =
	"Both are required by this console. Enter them in your own time; what X is sent is shown under each field, in UTC to the second.";

export const SCHEDULE_PREFILLED =
	"Prefilled: a start fifteen minutes from now and an end seven days after it. Change either.";

/** Under a time field: exactly what goes on the wire, or that nothing would. */
export function sentAs(iso: string | null): string {
	return iso === null ? "Not a time yet — nothing would be sent." : `Sent to X as ${iso}`;
}

// ── Step 3: audience ────────────────────────────────────────────────

export const AUDIENCE_HELP =
	"Countries, by name. Type to search X's list; each country you add becomes one targeting criterion, sent one request at a time after the line item.";

export const COUNTRY_LABEL = "Country";
export const COUNTRY_PLACEHOLDER = "Type a country's name";
export const SEARCHING = "Asking X…";
export const NO_MATCH = "X matched no country for that.";
export const ADD_COUNTRY = "Add";
export const REMOVE_COUNTRY = "Remove";
export const COUNTRIES_CHOSEN = "Chosen:";

/**
 * 🔴 Excluded countries (the owner, 2026-10-29): each one is a LOCATION criterion sent with
 * `operator_type` NE. The reference names NE for keywords and app lists and **does not say**
 * it applies to a place (audits/2026-10-29/x-reference-targeting-v2.md), so the sentence says
 * what is sent and that X's answer decides — never that X keeps the line item out.
 */
export const EXCLUDE_COUNTRY = "Exclude";
export const COUNTRIES_EXCLUDED = "Excluded:";
export const EXCLUDE_HELP =
	"Exclude sends the country as a targeting criterion with operator NE, after the countries chosen. X's reference names NE for keywords and app lists and does not say it applies to a place, so whether X takes it is X's answer: a refusal stops the run at that criterion.";

/**
 * The audience step's other three fields (2026-10-23, Ads Manager's ad-group form:
 * `audits/2026-10-23/adgroup-fields-spec.md`, PR 2). Each is optional; only GENDER's
 * empty choice has X's word behind it — «Leave null to target all» — so the sentence
 * says what is sent, and claims X's reading only for gender.
 */
export const AUDIENCE_MORE_HELP =
	"Language, gender and age are optional. Each one chosen becomes one more targeting criterion, sent after the countries; one left empty sends nothing for it. For gender X says: «Leave null to target all».";
export const LANGUAGE_LABEL = "Add a language";
export const LANGUAGE_NONE = "Choose a language";
export const LANGUAGES_NOT_READ = "X's languages could not be read.";
export const LANGUAGES_CHOSEN = "Languages:";
export const GENDER_LABEL = "Gender";
export const GENDER_ALL = "All — no gender criterion";
export const AGE_LABEL = "Age";
export const AGE_ALL = "All — no age criterion";

/**
 * Operating system, Device model and Carrier (2026-10-23, PR 3 of the same spec). Each
 * is optional and, like the three above, sends nothing when left empty; what X then
 * reaches is X's to say, so these sentences say only what is sent.
 */
export const DEVICE_MORE_HELP =
	"Operating system, device model and carrier are optional too. Each one chosen becomes one more targeting criterion, sent after the age bucket; one left empty sends nothing for it.";
export const OS_LABEL = "Add an operating system";
export const OS_NONE = "Choose an operating system";
export const OS_NOT_READ = "X's operating systems could not be read.";
export const OS_CHOSEN = "Operating systems:";
export const DEVICE_SEARCH_LABEL = "Search device models";
export const DEVICE_LABEL = "Add a device model";
export const DEVICE_NONE = "Choose a device model";
export const DEVICES_NOT_READ = "X's device models could not be read.";
export const DEVICES_CHOSEN = "Device models:";
export const CARRIER_COUNTRY_LABEL = "Carriers in";
export const CARRIER_LABEL = "Add a carrier";
export const CARRIER_NONE = "Choose a carrier";
export const CARRIERS_NOT_READ = "X's carriers could not be read.";
export const CARRIERS_CHOSEN = "Carriers:";
/**
 * 🔴 Why the carrier list waits for a country: X lists carriers per country and, asked
 * with none, answers its US list — «If this parameter is not specified only partner
 * audiences for the United States are returned» (reference R:6409–6500).
 */
export const CARRIER_NEEDS_COUNTRY =
	"Carriers wait for a country above: X lists carriers per country, and the list is read for the country chosen here.";

/**
 * Custom audiences (2026-10-24, PR 7 of all-fields-spec.md). Only audiences X reports as
 * targetable are offered — the reference: «To target a Custom Audience, that audience must
 * be targetable» — and the rest are listed with X's own reasons, untranslated, because the
 * reference names no list of them. Only the account's own: shared ones are not read.
 */
export const AUDIENCES_HELP =
	"Custom audiences are optional. Only this account's own audiences that X reports as targetable can be chosen; each one chosen becomes one more targeting criterion, sent after the look-alikes. Most likely it widens the audience, as interests do: X's guide puts «Tailored Audiences» among the «Primary» types it unions, and X's Audiences page links «Custom Audiences» to its tailored-audiences page — not observed here.";
export const AUDIENCE_LABEL = "Add a custom audience";
export const AUDIENCE_NONE = "Choose a custom audience";
export const AUDIENCES_NOT_READ = "X's custom audiences for this account could not be read.";
export const AUDIENCES_CHOSEN = "Custom audiences:";
export const AUDIENCES_EMPTY = "X lists no custom audiences of this account's own.";
export const AUDIENCES_NOT_TARGETABLE = "Not targetable, as X reports them:";

/** An audience X reports as not targetable, with X's reasons as X wrote them. */
export function audienceNotTargetable(a: {
	name: string;
	reasonsNotTargetable: readonly string[];
}): string {
	return a.reasonsNotTargetable.length === 0
		? `${a.name} — X gives no reason`
		: `${a.name} — ${a.reasonsNotTargetable.join(", ")}`;
}

export const EVERYONE_LABEL = "Everyone, no geography";

/**
 * The checkbox's label beside exclusions (targeting v2; the reviewer's 🔵 on #242). With a
 * country excluded, «Everyone» would say whom the line item reaches, which nobody here has
 * seen, and «no geography» would be false — an exclusion is geography. So it names what is
 * sent and leaves the reach to X, as ONLY_EXCLUSIONS does.
 */
export function everyoneLabel(excluded: readonly string[]): string {
	return excluded.length === 0
		? EVERYONE_LABEL
		: `No country targeted, ${excluded.join(", ")} excluded — whom X reaches then, the reference does not say`;
}

/**
 * 🔴 The reach of an empty list, in X's words and with the attribution, as
 * NO_CRITERIA in targeting-copy.ts carries it. The checkbox is the deliberate way
 * past the check in `stepProblem(3)`: an empty audience has to be chosen, not
 * arrived at.
 */
export const EVERYONE_NOTE =
	"X's documentation says a line item with no targeting criteria targets all users worldwide (campaign-management, the Targeting section, read 2026-10-07). Ticking this sends no country to target, on purpose — only the countries excluded, if any. Without a tick and without a country the next step does not open.";

// ── Step 4: ad ──────────────────────────────────────────────────────

export const AD_HELP =
	"The ad is a tweet. Write one here — your words and up to four images, or one GIF — or use a PUBLISHED tweet the account already has.";

export const AD_MODES = {
	write: "Write the ad",
	existing: "Use an existing tweet",
} as const;

export const WRITE_HELP =
	"What you write becomes a nullcast tweet: created for this ad, not shown on the account's timeline, and served only when promoted. It is made when you press Create (paused), after the campaign and the line item.";

export const AD_TEXT_LABEL = "Text";

/** Under the text: a count, and whose limit it is — X's, which this console has not read. */
export function textCounter(text: string): string {
	const n = [...text].length;
	return `${n} ${n === 1 ? "character" : "characters"}. The limit is X's; a text X refuses comes back with X's words.`;
}

export const AD_NAME_LABEL = "Name";
export const AD_NAME_HELP = `Optional, up to ${AD_NAME_MAX} characters — X's \`name\` for the tweet. Empty means the opening words of the text.`;

export const AD_URL_LABEL = "Website URL";

/**
 * Under the link field: required or optional by the objective, with the observation
 * that made it required quoted as X's own words (2026-10-21, attest-creatives.txt).
 */
export function urlHelp(objective: LineItemObjective): string {
	return urlRequired(objective)
		? "Required under WEBSITE_CLICKS: X refused to promote a tweet without a link under this objective — «INVALID: Tweet : 2112870999612178835 not appropriate for objective WEBSITE_CLICKS» (2026-10-21, request id cd0846f2f270f6e2). The link goes at the end of the tweet's text."
		: "Optional under ENGAGEMENTS. The link goes at the end of the tweet's text.";
}

/** X's rule for WEBSITE_CLICKS, verbatim (campaign-management guide, Objective Based Campaigns). */
export const CARD_OR_LINK = "«Tweet must have either a website card or a website link (not both).»";

/** The card's image limits, verbatim — the creatives guide, «Cards» → «Image». */
export const CARD_IMAGE_RULE =
	"X's guide: «Images must be 3MB or less and have a width of at least 800px» — for a website card «1:1 and 1.91:1» — «We support the following image formats: .bmp, .jpeg, and .png.»";

/** The two ways a written ad carries its website under WEBSITE_CLICKS; the card first, the owner's choice. */
export const WEBSITE_AS = {
	card: "Website card (image, headline, URL)",
	link: "Link in the text",
} as const;

export const WEBSITE_AS_HELP = `Under WEBSITE_CLICKS X's rule is one or the other: ${CARD_OR_LINK} The card is offered first because the owner asked for it; X's guide calls the card «recommended, but not required».`;

export const CARD_TITLE_LABEL = "Headline";
export const CARD_TITLE_HELP =
	"Shown under the image. X's reference gives the headline no length; if X refuses one, its words are shown as X sent them.";
export const CARD_URL_HELP =
	"Where the card takes people. It is the card's destination, not part of the tweet's text — the text carries no link beside a card.";
export const CARD_IMAGE_LABEL = "Image";
export const CARD_IMAGE_HELP = `One image. ${CARD_IMAGE_RULE} This console checks the size, the width and the type before uploading.`;

/** Outside ±1% of both ratios: a warning — the tolerance is this console's, X names none. */
export function cardRatioText(size: ImageSize): string {
	return `This image is ${size.width}×${size.height}. X's guide says «1:1 and 1.91:1» for a website card and names no tolerance; this console warns outside ±1% of either, and sends the image anyway — X decides.`;
}

/**
 * Who can reply (PR 8, 2026-10-24). 🔴 The options are X's own values, verbatim — the
 * reference names them («`EVERYONE`, `FOLLOWING`, `MENTIONED_USERS`») and says what the field
 * is for, not what each value means, so no gloss is written here. Ads Manager's «Everyone can
 * reply» is this field by its meaning — a hypothesis, not a line of the reference.
 */
export const REPLIES_LABEL = "Who can reply";
export const REPLIES_OPTIONS = [
	{ value: "", label: "X's default (EVERYONE) — nothing sent" },
	{ value: "EVERYONE", label: "EVERYONE" },
	{ value: "FOLLOWING", label: "FOLLOWING" },
	{ value: "MENTIONED_USERS", label: "MENTIONED_USERS" },
] as const;
export const REPLIES_HELP =
	"X's reference: «Choose who can reply to this Tweet. Anyone mentioned can always reply.» Sent only when chosen; X answers the value on a read of the tweet, not on its create, and the console reads it back.";

export const MEDIA_LABEL = "Media";

/**
 * Under the file field. The video sentence is the spec's (rev. 4 §1), with no «yet»:
 * a boundary, not a promise.
 */
export const MEDIA_HELP = `Up to ${AD_MEDIA_MAX} images (JPG, PNG, WEBP; 5 MB each) or one GIF (15 MB, at most 1280x1080) — the reference's «up to 4 images, 1 animated GIF, or 1 video», read as the two shapes this console offers. Files are uploaded when you press Create (paused), not now.`;

export const MEDIA_VIDEO_NOTE =
	"video: not offered — this console uploads images and GIFs; video has no decision on size";

export const REMOVE_FILE = "Remove";

export const EXISTING_HELP =
	"One PUBLISHED tweet becomes the ad. List the account's timeline, or read a tweet by its id; either way what you pick is X's row, with its text in front of you.";

/**
 * Under the pick, when the line item is WEBSITE_CLICKS and the picked tweet's text has no
 * http(s) (`linkUnseen`). A warning: the step lets the tweet through, because a website
 * card is X's other answer and this console does not read cards.
 */
export const EXISTING_NO_LINK = {
	title: "This tweet's text has no link — X may refuse it under WEBSITE_CLICKS.",
	body: "X's rule for this objective: «Tweet must have either a website card or a website link (not both).» This console reads a tweet's text, and card_uri is not read, so it cannot see whether this tweet has a website card. X refused a promote without a website link on 2026-10-21 (attest-creatives.txt:50).",
} as const;

export const TIMELINE_LABEL = "Which timeline";
export const TIMELINE_PLACEHOLDER = "Choose a timeline";
export const LIST_TWEETS = "List tweets";
export const TWEET_ID_LABEL = "Tweet id";
export const TWEET_ID_HELP =
	"Digits, as X writes it. The id is a question to X; what appears is X's answer, and that row is what you pick.";
export const FIND_TWEET = "Find by id";
export const NO_TWEETS = "X returned no PUBLISHED tweet for that.";
export const PICK_TWEET = "Pick one:";

// ── Step 5: review, the run, and Start ──────────────────────────────

export const REVIEW_HELP = "What will be sent, in words. Nothing is created until Create (paused).";

export const REVIEW_ROWS = {
	account: "Ad account",
	campaign: "Campaign",
	instrument: "Paid by",
	budget: "Budget",
	adGroup: "Ad group",
	schedule: "Flight",
	countries: "Countries",
	excludedCountries: "Excluded countries",
	languages: "Languages",
	gender: "Gender",
	age: "Age",
	operatingSystems: "Operating systems",
	devices: "Device models",
	carriers: "Carriers",
	audiences: "Custom audiences",
	tweet: "Tweet",
	ad: "Ad",
} as const;

/** The review row for an ad written here: what the tweet will say, and what it carries. */
export function writtenAdSummary(
	text: string,
	name: string,
	files: readonly Pick<File, "name">[],
): string {
	const words = text.trim() === "" ? "no text" : `«${text.trim()}»`;
	const media =
		files.length === 0
			? "no media"
			: `${files.length} ${files.length === 1 ? "file" : "files"}: ${files.map((f) => f.name).join(", ")}`;
	return `${words} — name «${name}» — ${media}. Made as a nullcast tweet at Create (paused).`;
}

/** Both figures through money.ts, with the instrument's code beside each. */
export function budgetSummary(
	daily: number | null,
	total: number | null,
	currency: string | null,
): string {
	const one = (micro: number | null) => (micro === null ? "none" : microMoney(micro, currency));
	return `daily ${one(daily)}, total ${one(total)}`;
}

/** Your time and X's, side by side, so the row reads the same as the fields did. */
export function scheduleSummary(
	start: { local: string; iso: string | null },
	end: { local: string; iso: string | null },
): string {
	const utc = (iso: string | null) => iso ?? "not a time";
	return `${start.local} → ${end.local} in your time; ${utc(start.iso)} → ${utc(end.iso)} as sent`;
}

export function countriesSummary(
	names: readonly string[],
	everyone: boolean,
	excluded: readonly string[] = [],
): string {
	// «No country is targeted», not «no criterion»: an excluded country is one (2026-10-29).
	// Beside exclusions, no «Everyone» and no «no geography» (everyoneLabel above).
	if (everyone)
		return excluded.length === 0
			? "Everyone, no geography — no country is targeted."
			: `No country is targeted; ${excluded.join(", ")} excluded — whom X reaches then, the reference does not say.`;
	return names.join(", ");
}

/** A review row for an optional criterion: the chosen names, or that nothing is sent. */
export function optionalSummary(names: readonly string[]): string {
	return names.length === 0 ? "None — no criterion is sent." : names.join(", ");
}

export const CREATE_LABEL = "Create (paused)";

export const RUN_ORDER =
	"In order, one at a time: the campaign, then the line item under it, then each country, language, gender, age bucket, operating system, device model, carrier, interest, keyword, look-alike and custom audience as its own targeting criterion, then — when the ad is written here — each file as its own upload and the tweet, then the tweet as the ad. Each step waits for X's answer before the next is sent; a step that does not come back created stops the run there, and nothing is sent twice.";

export const RUN_ROWS = {
	campaign: "Campaign",
	lineItem: "Line item (ad group)",
	targeting: "Targeting",
	media: "Upload",
	card: "Website card",
	tweet: "Tweet",
	ad: "Ad (promoted tweet)",
} as const;

/**
 * 🔴 X's review on the ad's line, dated to the create: on 2026-10-21 the same tweet's
 * promoted tweets read ACCEPTED at 08:57Z and REJECTED at 11:38Z
 * (`audits/2026-10-21/attest-creatives.txt`), so the status the attach answered is a
 * status at creation and not a standing one. The line item's Ads panel reads it again
 * when opened; this line says so rather than letting the word stand as the present. Until
 * PR 3 of the one-screen layout (2026-10-29) it named «the Creative panel on the line
 * item's row» — a place that moved to the line item's own screen.
 */
export function reviewAtCreation(approvalStatus: string | null): string {
	return `At creation: ${approvalNote(approvalStatus)} X's review can change afterwards — a creative ACCEPTED at 08:57Z read REJECTED at 11:38Z on 2026-10-21 — and the line item's Ads panel reads it again when opened.`;
}

/** The tweet that exists after a stopped run — no main copy says it, so it is said here. */
/** A card that exists after a stopped run — the card's own words, as the tweet's are below. */
export const CARD_STOPPED =
	"The card exists — do not retry; a retry creates a second card. Its id is on this line.";

export const TWEET_STOPPED =
	"The tweet exists — do not retry, look it up with GET /api/v1/tweets?type=PUBLISHED&ids=; a retry creates a second tweet.";

/** An upload that stopped: nothing to look up, and nothing that costs. */
export const MEDIA_STOPPED =
	"The upload did not go through; nothing was made that costs or that has to be found.";

/** Under an upload X is still processing: what the run does, in X's own terms. */
export function processingText(state: string | null | undefined): string | null {
	if (state === null || state === undefined || state === "succeeded") return null;
	if (state === "failed") return "X reports failed for this upload.";
	return `X reports ${state}: the run asks X again before the tweet is made.`;
}

/**
 * 🔴 The number beside a created campaign, with where it comes from in the sentence
 * — the observations are `create-ad.ts`'s header: two from Ads Manager's screen and,
 * since 2026-10-21, one from X's API (`campaign_id=42817103` for `phpvz`, in X's own
 * refusal). Shown for the campaign and for nothing else; the campaign card borrows
 * this sentence rather than wording a second one.
 */
export function adsManagerText(campaignId: string): string | null {
	const n = adsManagerNumber(campaignId);
	if (n === null) return null;
	return `Ads Manager shows this campaign as ${n}: the API id read as base36, as Ads Manager showed it on 2026-10-08 and 2026-10-19, and as X's API named a campaign on 2026-10-21.`;
}

/** The new sentence for a criterion that exists after a stopped run — no main copy says it. */
export const CRITERION_STOPPED =
	"The criterion exists — do not retry, look it up on the line item's Targeting panel.";

/** The same, for the promoted tweet. */
export const AD_STOPPED =
	"The promoted tweet exists — do not retry, look it up in the line item's Ads panel.";

export const MAY_EXIST =
	"X did not answer this step, so it may exist — do not retry; look before sending anything again.";

/**
 * One step's line. A stopped campaign or line item speaks with the contract's own
 * words, borrowed from the two copy modules whose tests check them.
 */
export function stepText(
	what: keyof typeof RUN_ROWS,
	state: StepState,
): { text: string; detail: string | null } {
	switch (state.kind) {
		case "pending":
			return { text: "Not sent.", detail: null };
		case "running":
			return { text: "Asking X…", detail: null };
		case "done":
			return {
				text:
					what === "media"
						? `Uploaded — media key ${state.id}`
						: what === "card"
							? `Created ${state.id} — ${state.cardUri ?? "no card_uri"}`
							: `Created ${state.id}`,
				detail:
					what === "campaign"
						? adsManagerText(state.id)
						: what === "media"
							? processingText(state.processing)
							: what === "ad"
								? reviewAtCreation(state.approvalStatus ?? null)
								: (state.note ?? null),
			};
		case "stopped": {
			if (state.why === "refused") return { text: `Not created: ${state.error}`, detail: null };
			const id = state.id ? ` (${state.id})` : "";
			if (state.why === "unknown") {
				const words =
					what === "campaign"
						? `${CAMPAIGN_UNKNOWN.title} ${CAMPAIGN_UNKNOWN.body}`
						: what === "lineItem"
							? `${LINE_ITEM_UNKNOWN.title} ${LINE_ITEM_UNKNOWN.body}`
							: what === "media"
								? MEDIA_STOPPED
								: MAY_EXIST;
				return { text: `${words}${id}`, detail: state.error };
			}
			const words =
				what === "campaign"
					? `${CAMPAIGN_EXISTS.title} ${CAMPAIGN_EXISTS.body}`
					: what === "lineItem"
						? `${LINE_ITEM_EXISTS.title} ${LINE_ITEM_EXISTS.body}`
						: what === "targeting"
							? CRITERION_STOPPED
							: what === "card"
								? CARD_STOPPED
								: what === "tweet"
									? TWEET_STOPPED
									: what === "media"
										? MEDIA_STOPPED
										: AD_STOPPED;
			return { text: `${words}${id}`, detail: state.error };
		}
	}
}

/**
 * The banner after a run that stopped: what is in X, and that a second press makes
 * a second one of each. With nothing made and nothing that may be, the draft may be
 * sent again. Until PR 3 of the one-screen layout (2026-10-29) the body ended «the line item
 * form, Targeting and Creative sit under the campaign's row»; they are in the Line items
 * panel now, and the line item's own screen.
 */
export function stoppedText(
	created: readonly { what: string; id: string }[],
	mayExist: boolean,
): { title: string; body: string } {
	if (created.length === 0 && !mayExist) {
		return {
			title: "Nothing was created.",
			body: "X refused the step marked above; nothing from this press is in X. Fix it and press Create (paused) again.",
		};
	}
	const list = created.map((c) => `${c.what} ${c.id}`).join(", ");
	return {
		title: "Stopped — do not retry, look the ids up.",
		body: `${list ? `In X now: ${list}. ` : ""}${
			mayExist ? "The step that stopped may also exist: X did not say. " : ""
		}A second press would create a second one of each, so Create stays off for this draft. Open in Campaigns and go on from there: Line items on the campaign's row opens its line items and the New line item form, and Open on a line item's row its targeting, its Start and its ads.`,
	};
}

export const OPEN_IN_CAMPAIGNS = "Open in Campaigns";

export const DONE = {
	title: "Created PAUSED. Everything above is in X now.",
	body: "Nothing serves yet: the campaign and the line item are PAUSED until Start below, and X decides whether it serves afterwards.",
};

export const START_HEADING = "Start";

export const START_ORDER =
	"Two presses, in order: Start ad group, then Start campaign — the second is offered once X reports the ad group ACTIVE. Both were created PAUSED and stay so until pressed. What X reports is read back under each button.";

export const START_AD_GROUP = "Start ad group";
export const START_CAMPAIGN = "Start campaign";

export const START_CAMPAIGN_WAITS = "Offered once X reports the ad group ACTIVE.";

/** A problem, worded. The kinds are `create-ad.ts`'s. */
export function problemText(p: Problem): string {
	switch (p) {
		case "account":
			return "Choose an ad account.";
		case "name":
			return "A campaign name is required.";
		case "instrument":
			return "Choose the funding instrument that pays.";
		case "daily-missing":
			return "A daily budget is required on this screen. X has accepted campaigns with none — ph9ff (read 2026-10-19) and phbin (read 2026-10-21) carry daily and total null in the API — but the console's own run of 2026-10-21 sent one, and under AUTO X optimises the bid against it. A create without a budget has not been sent from this console.";
		case "daily-invalid":
		case "total-invalid":
		case "ad-group-total-invalid":
		case "ad-group-daily-invalid":
			return "An amount, 0 or more, with up to two decimals — 10 or 10.50. Nothing else is sent.";
		case "ad-group-daily-missing":
			return "A daily budget for the ad group is required with the budget on each ad group — Ads Manager refused an ad group without one («Ad group 1: Daily budget is required», 2026-10-23).";
		case "start":
			return "Start is not a time.";
		case "end":
			return "End is not a time.";
		case "end-before-start":
			return "End must come after Start.";
		case "no-country":
			return "Add a country, or tick the box to target no country on purpose.";
		case "too-many-interests":
			return `More than ${PRIMARY_LIMITS.interests} interests — X: «You can target up to 100 interests.»`;
		case "too-many-keywords":
			return `More than ${PRIMARY_LIMITS.keywords} keywords — X: «You can use up to 1000 keywords for targeting (per line item).»`;
		case "too-many-lookalikes":
			return `More than ${PRIMARY_LIMITS.lookalikes} users for look-alikes — X: «You can use up to 100 Users.»`;
		case "no-tweet":
			return "Pick one tweet.";
		case "ad-empty":
			return "Write some text, or add a file — a tweet needs one of the two (the reference: text is required if no media_keys are specified).";
		case "ad-name":
			return `The name is longer than ${AD_NAME_MAX} characters, the reference's maximum.`;
		case "ad-url":
			return "A website URL is required under WEBSITE_CLICKS: X refused a promote without one on 2026-10-21 («not appropriate for objective WEBSITE_CLICKS»).";
		case "ad-url-invalid":
			return "The website URL is not an http(s) address the browser can read.";
		case "media-count":
			return `At most ${AD_MEDIA_MAX} files: the reference allows up to 4 images on one tweet.`;
		case "media-gif-alone":
			return "A GIF goes alone: the reference allows 1 animated GIF, or up to 4 images — not both.";
		case "media-type":
			return "A file is not JPG, PNG, WEBP or GIF — the types this console uploads.";
		case "media-size":
			return "A file is over the reference's limit for its kind (5 MB for an image, 15 MB for a GIF), or empty.";
		case "bid-amount-missing":
			return "A bid amount is required for a maximum bid or a target cost — X: «Required if bid_strategy is set to either MAX or TARGET.»";
		case "bid-amount-invalid":
			return "An amount above 0, with up to two decimals — X: «Only values greater than zero are accepted.»";
		case "placements-empty":
		case "placements-all-alone":
		case "placements-profile-alone":
			return PLACEMENT_PROBLEMS[p];
		case "search-needs-keyword":
			return SEARCH_NEEDS_KEYWORD;
		case "cap-without-window":
		case "window-without-cap":
		case "cap-invalid":
			return DELIVERY_PROBLEMS[p];
		case "card-title":
			return "The card needs a headline: X's card shows it under the image (DETAILS requires a title).";
		case "card-text-link":
			return `The tweet's text carries a link, and the website is the card's: ${CARD_OR_LINK}`;
		case "card-image-count":
			return "A website card takes one image — choose exactly one file.";
		case "card-image-type":
			return `The card's image is not JPG or PNG. ${CARD_IMAGE_RULE} (.bmp is X's too; this console's upload does not take it.)`;
		case "card-image-size":
			return `The card's image is over 3 MB, or empty. ${CARD_IMAGE_RULE}`;
		case "card-image-unread":
			return "The image's size has not been read yet — wait a moment, or choose it again.";
		case "card-image-width":
			return `The card's image is narrower than 800px. ${CARD_IMAGE_RULE}`;
	}
}
